import type { MenuItem, OptionGroup } from './types';

/** One tray line: a dish with its chosen options. Same dish + same options + same note = same line. */
export interface CartLine {
  key: string;
  itemId: string;
  qty: number;
  choices: Record<string, string[]>;
  note?: string;
}

export const MAX_QTY = 50;
export const MAX_LINES = 30;

export function lineKey(itemId: string, choices: Record<string, string[]>, note?: string): string {
  const c = Object.keys(choices).sort().map((g) => `${g}=${[...choices[g]].sort().join('+')}`).join(';');
  return `${itemId}|${c}|${(note ?? '').trim()}`;
}

/** Same rule the server applies; the server re-checks and is the source of truth for prices. */
export function unitPrice(item: MenuItem, choices: Record<string, string[]>): number {
  let p = item.priceSatang;
  for (const g of item.options ?? []) for (const id of choices[g.id] ?? []) p += g.choices.find((c) => c.id === id)?.priceSatang ?? 0;
  return p;
}

/** null when valid, otherwise the first group that needs attention. */
export function missingChoice(item: MenuItem, choices: Record<string, string[]>): OptionGroup | null {
  for (const g of item.options ?? []) {
    const n = (choices[g.id] ?? []).length;
    if (n < g.min || n > g.max) return g;
  }
  return null;
}

/** Tap on a choice: single-pick groups swap, multi-pick groups toggle up to max. */
export function toggleChoice(group: OptionGroup, current: string[], choiceId: string): string[] {
  if (group.max === 1) return current[0] === choiceId && group.min === 0 ? [] : [choiceId];
  if (current.includes(choiceId)) return current.filter((c) => c !== choiceId);
  return current.length >= group.max ? current : [...current, choiceId];
}

export function addToCart(cart: CartLine[], itemId: string, choices: Record<string, string[]>, qty: number, note?: string): CartLine[] {
  const key = lineKey(itemId, choices, note);
  const hit = cart.find((l) => l.key === key);
  if (hit) return cart.map((l) => (l.key === key ? { ...l, qty: Math.min(MAX_QTY, l.qty + qty) } : l));
  if (cart.length >= MAX_LINES) return cart;
  return [...cart, { key, itemId, qty: Math.min(MAX_QTY, qty), choices, note: note?.trim() || undefined }];
}

export function setQty(cart: CartLine[], key: string, qty: number): CartLine[] {
  return qty <= 0 ? cart.filter((l) => l.key !== key) : cart.map((l) => (l.key === key ? { ...l, qty: Math.min(MAX_QTY, qty) } : l));
}

export function cartTotal(cart: CartLine[], items: Map<string, MenuItem>): number {
  return cart.reduce((s, l) => {
    const it = items.get(l.itemId);
    return it ? s + unitPrice(it, l.choices) * l.qty : s;
  }, 0);
}

export const toOrderBody = (cart: CartLine[]) => ({
  items: cart.map((l) => ({ itemId: l.itemId, qty: l.qty, choices: l.choices, ...(l.note ? { note: l.note } : {}) })),
});
